/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
import { cva, VariantProps } from 'class-variance-authority'
import React, { HTMLAttributes } from 'react'

import { cn } from '@/utils/css'

/** Variants for the container component. */
const containerVariants = cva('relative flex *:relative', {
  variants: {
    maxWidth: {
      '3xs': 'max-w-3xs',
      '2xs': 'max-w-2xs',
      xs: 'max-w-xs',
      sm: 'max-w-sm',
      md: 'max-w-md',
      lg: 'max-w-lg',
      xl: 'max-w-xl',
      '2xl': 'max-w-2xl',
      '3xl': 'max-w-3xl',
      '4xl': 'max-w-4xl',
      '5xl': 'max-w-5xl',
      '6xl': 'max-w-6xl',
      '7xl': 'max-w-7xl',
      '8xl': 'max-w-8xl',
      full: 'max-w-full',
    },
    padding: {
      none: 'px-0 py-0',
      xs: 'px-2 py-1',
      sm: 'px-4 py-2',
      md: 'px-6 py-3',
      lg: 'px-8 py-4',
      xl: 'px-12 py-6',
      '2xl': 'px-16 py-8',
      '3xl': 'px-20 py-10',
      '4xl': 'px-24 py-12',
      '5xl': 'px-32 py-16',
    },
    paddingX: {
      none: 'px-0',
      xs: 'px-2',
      sm: 'px-4',
      md: 'px-6',
      lg: 'px-8',
      xl: 'px-12',
      '2xl': 'px-16',
      '3xl': 'px-20',
      '4xl': 'px-24',
      '5xl': 'px-32',
    },
    paddingY: {
      none: 'py-0',
      xs: 'py-1',
      sm: 'py-2',
      md: 'py-3',
      lg: 'py-4',
      xl: 'py-6',
      '2xl': 'py-8',
      '3xl': 'py-10',
      '4xl': 'py-12',
      '5xl': 'py-16',
    },
    marginX: {
      none: 'mx-0',
      xs: 'mx-2',
      sm: 'mx-4',
      md: 'mx-6',
      lg: 'mx-8',
      xl: 'mx-12',
      '2xl': 'mx-16',
      '3xl': 'mx-20',
      '4xl': 'mx-24',
      '5xl': 'mx-32',
    },
    marginY: {
      none: 'my-0',
      xs: 'my-2',
      sm: 'my-4',
      md: 'my-6',
      lg: 'my-8',
      xl: 'my-12',
      '2xl': 'my-16',
      '3xl': 'my-20',
      '4xl': 'my-24',
      '5xl': 'my-32',
    },
    direction: {
      row: 'flex-row',
      column: 'flex-col',
      rowReverse: 'flex-row-reverse',
      columnReverse: 'flex-col-reverse',
    },
    justify: {
      start: 'justify-start',
      end: 'justify-end',
      center: 'justify-center',
      between: 'justify-between',
      around: 'justify-around',
      evenly: 'justify-evenly',
    },
    align: {
      start: 'items-start',
      end: 'items-end',
      center: 'items-center',
      baseline: 'items-baseline',
      stretch: 'items-stretch',
    },
    wrap: {
      nowrap: 'flex-nowrap',
      wrap: 'flex-wrap',
      wrapReverse: 'flex-wrap-reverse',
    },
    gap: {
      none: 'gap-0',
      xs: 'gap-1',
      sm: 'gap-2',
      md: 'gap-4',
      lg: 'gap-6',
      xl: 'gap-8',
      '2xl': 'gap-10',
      '3xl': 'gap-12',
      '4xl': 'gap-16',
      '5xl': 'gap-20',
    },
    background: {
      'bg-none': 'bg-none',
      'bg-transparent': 'bg-transparent',
      'bg-white': 'bg-white',
      'bg-black': 'bg-black',
      'bg-primary-50': 'bg-primary-50',
      'bg-primary-100': 'bg-primary-100',
      'bg-primary-200': 'bg-primary-200',
      'bg-primary-300': 'bg-primary-300',
      'bg-primary-400': 'bg-primary-400',
      'bg-primary-500': 'bg-primary-500',
      'bg-primary-600': 'bg-primary-600',
      'bg-primary-700': 'bg-primary-700',
      'bg-primary-800': 'bg-primary-800',
      'bg-primary-900': 'bg-primary-900',
      'bg-gray-50': 'bg-gray-50',
      'bg-gray-100': 'bg-gray-100',
      'bg-gray-200': 'bg-gray-200',
      'bg-gray-300': 'bg-gray-300',
      'bg-gray-400': 'bg-gray-400',
      'bg-gray-500': 'bg-gray-500',
      'bg-gray-600': 'bg-gray-600',
      'bg-gray-700': 'bg-gray-700',
      'bg-gray-800': 'bg-gray-800',
      'bg-gray-900': 'bg-gray-900',
      'bg-secondary-50': 'bg-secondary-50',
      'bg-secondary-100': 'bg-secondary-100',
      'bg-secondary-200': 'bg-secondary-200',
      'bg-secondary-300': 'bg-secondary-300',
      'bg-secondary-400': 'bg-secondary-400',
      'bg-secondary-500': 'bg-secondary-500',
      'bg-secondary-600': 'bg-secondary-600',
      'bg-secondary-700': 'bg-secondary-700',
      'bg-secondary-800': 'bg-secondary-800',
      'bg-secondary-900': 'bg-secondary-900',
      'bg-graphite-50': 'bg-graphite-50',
      'bg-graphite-100': 'bg-graphite-100',
      'bg-graphite-200': 'bg-graphite-200',
      'bg-graphite-300': 'bg-graphite-300',
      'bg-graphite-400': 'bg-graphite-400',
      'bg-graphite-500': 'bg-graphite-500',
      'bg-graphite-600': 'bg-graphite-600',
      'bg-graphite-700': 'bg-graphite-700',
      'bg-graphite-800': 'bg-graphite-800',
      'bg-graphite-900': 'bg-graphite-900',
    },
    shadow: {
      none: 'shadow-none',
      sm: 'shadow-xs',
      md: 'shadow-sm',
      lg: 'shadow-lg',
    },
    overflow: {
      visible: 'overflow-visible',
      hidden: 'overflow-hidden',
      scroll: 'overflow-scroll',
      auto: 'overflow-auto',
    },
    fullWidth: {
      true: 'w-full',
      false: '',
    },
    fullHeight: {
      true: 'h-full',
      false: '',
    },
    mxAuto: {
      true: 'mx-auto',
      false: '',
    },
  },
  defaultVariants: {
    direction: 'column',
    justify: 'start',
    align: 'stretch',
    wrap: 'nowrap',
    gap: 'none',
    background: 'bg-none',
    shadow: 'none',
    overflow: 'visible',
  },
})

/**
 * Variants for the rounding of the container component.
 * Separated to allow the background image & gradient overlay to be rounded.
 */
const roundedVariants = cva('', {
  variants: {
    rounded: {
      none: 'rounded-none',
      sm: 'rounded-xs',
      md: 'rounded-sm',
      lg: 'rounded-lg',
      xl: 'rounded-xl',
      '2xl': 'rounded-2xl',
      '3xl': 'rounded-3xl',
      full: 'rounded-full',
    },
  },
  defaultVariants: {
    rounded: 'none',
  },
})

/** A description of available properties for this component. */
type ContainerProps = HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof containerVariants> &
  VariantProps<typeof roundedVariants> & {
    /** A background image to display in the container. */
    backgroundImage?: string

    /** The minimum height of the container. */
    minimumHeight?: string

    /** Whether to apply a gradient overlay to the container. */
    gradientOverlay?: boolean
  }

/**
 * A customisable container component that can be used to wrap content.
 */
const Container: React.FC<ContainerProps> = ({
  className,
  maxWidth,
  padding,
  paddingX,
  paddingY,
  marginX,
  marginY,
  direction,
  justify,
  align,
  wrap,
  gap,
  minimumHeight,
  background,
  backgroundImage,
  rounded,
  shadow,
  overflow,
  fullWidth,
  fullHeight,
  mxAuto,
  gradientOverlay,
  children,
  ...props
}) => {
  const containerClasses = containerVariants({
    maxWidth,
    fullWidth,
    fullHeight,
    mxAuto,
    padding,
    paddingX,
    paddingY,
    marginX,
    marginY,
    direction,
    justify,
    align,
    wrap,
    gap,
    background,
    shadow,
    overflow,
    className,
  })
  const roundedClasses = roundedVariants({ rounded })

  return (
    <div style={{ minHeight: minimumHeight }} className={cn(containerClasses, roundedClasses)} {...props}>
      {backgroundImage && (
        <div
          style={{ backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined }}
          className={cn('absolute! inset-0 bg-center bg-cover', roundedClasses)}
        />
      )}
      {gradientOverlay && (
        <div className={cn('absolute! inset-0', roundedClasses, 'bg-linear-to-b from-transparent to-black/50')} />
      )}
      {children}
    </div>
  )
}

export { Container }
